import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/auth-context';
import { useClinicContext, clinicOptionLabel } from '@/dashboard/clinic-context';
import RoleHomeSearchPortal from '@/dashboard/ui/RoleHomeSearchPortal';
import ReceptionSearch from '@/dashboard/patients/ReceptionSearch';
import { walkinBoardHref } from '@/dashboard/patients/GlobalPatientSearchModal';
import MasterDetail, { DetailAside } from '@/dashboard/ui/MasterDetail';
import {
  advanceLeadStage,
  fetchMyWorkActions,
  revealLeadContact,
  transitionWorkAction,
} from '@/dashboard/api';
import type { LeadStageTarget } from '@/dashboard/api';
import { useDashboardMeContext } from '@/dashboard/me-context';
import LeadContactRevealControl from '@/dashboard/owner/LeadContactReveal';
import { AcButton } from '@/dashboard/ui';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import LeadQuickViewHost from '@/dashboard/leads/LeadQuickViewHost';
import QuickViewStageStrip from '@/dashboard/leads/QuickViewStageStrip';
import {
  applyStatusToRow,
  countByReason,
  countByStatusFilter,
  DEFAULT_WORKBOARD_FILTERS,
  applyStageAdvanceToRows,
  distinctReasons,
  filterWorkActions,
  formatRelativeAge,
  isConflictError,
  isOpenStatus,
  ROW_STAGE_LABEL,
  stageAwareActions,
  stageTargetLabel,
  statusBadge,
  summarizeWorkActions,
  workboardActionErrorMessage,
  workboardGuidance,
  workboardLeadHref,
  type FollowupRow,
  type WorkActionTransition,
  type WorkboardFilters,
  type WorkboardStatusFilter,
} from '@/dashboard/workboard/workboard-view';

/**
 * AdoraCare - C2 front-half operator Workboard (C2-WORKBOARD-UI-BUILD-01).
 *
 * The first true staff work-queue screen. It reads the caller's assigned
 * follow-ups from GET /api/me/work-actions (open lead_actions for the caller's
 * role-or-self, clinic-fenced server-side), and gives each row the work-action
 * transitions (Done / Dismiss -> POST /api/work-actions/:id/transition) plus the
 * FRONT-HALF lead stage advances (Qualify / Booked / Arrived / Lost -> POST
 * /api/leads/:id/stage). It NEVER exposes the deferred patient /
 * converted_to_revenue positive-conversion gate (Phase E).
 *
 * Built to the contract, not the wishlist: the response carries no due_at,
 * clinic, assigned-role, priority, or current-stage field, so there is NO
 * overdue / due-today / clinic / role / priority / stage affordance here (see
 * workboard-view.ts HARD DATA RULE). Counters (open / completed / total),
 * the status + action-type filter pills, the name/lead search, and the Age
 * column are ALL derived from the eight fields the read actually returns plus
 * the statuses returned by the transition / stage endpoints.
 *
 * DATA HONESTY (mirrors WalkinFollowupsPanel): no sample rows, no PHI. Loading /
 * empty ("No assigned work right now.") / error (retry) / per-action 409
 * ("already updated by someone else") states are all truthful.
 *
 * Tested via react-dom/server (no jsdom): the pure WorkboardContent + the pure
 * helpers in workboard-view.ts are exercised with controlled props; the default
 * export's fetch fires in an effect (which SSR does not run), so the static
 * render is the loading state.
 */

// --- State + feedback types -------------------------------------------------

export type WorkboardPanelState =
  | { kind: 'loading' }
  | { kind: 'ready'; rows: FollowupRow[] }
  | { kind: 'error'; message: string };

export interface WorkboardFeedback {
  readonly kind: 'success' | 'error';
  readonly text: string;
  /** A concurrency conflict (409) -> offer a Refresh affordance. */
  readonly showRefresh?: boolean;
}

interface WorkboardContentProps {
  readonly state: WorkboardPanelState;
  readonly filters: WorkboardFilters;
  readonly busyId: number | null;
  readonly feedback: WorkboardFeedback | null;
  /** Injected so relative ages are deterministic in tests; the page passes Date.now(). */
  readonly nowMs: number;
  readonly onFilterChange: (filters: WorkboardFilters) => void;
  readonly onTransition: (id: number, status: WorkActionTransition) => void;
  readonly onAdvanceStage: (
    leadId: number,
    id: number,
    to: LeadStageTarget,
  ) => void;
  readonly onRetry: () => void;
  readonly onRefresh: () => void;
  /** STITCH-2: open the ONE LeadQuickView drawer for a row's lead. */
  readonly onQuickView?: (row: FollowupRow) => void;
}

const STATUS_FILTER_OPTIONS: ReadonlyArray<{
  value: WorkboardStatusFilter;
  label: string;
}> = [
  { value: 'open', label: 'Open' },
  { value: 'completed', label: 'Completed' },
  { value: 'all', label: 'All' },
];

function pillClass(active: boolean): string {
  return active ? 'workboard__pill workboard__pill--active' : 'workboard__pill';
}

// --- One work-queue row -----------------------------------------------------

export function WorkboardRow({
  row,
  busy,
  nowMs,
  onTransition,
  onAdvanceStage,
  onQuickView,
  notice,
}: {
  notice?: string | null;
  row: FollowupRow;
  busy: boolean;
  nowMs: number;
  onTransition: (id: number, status: WorkActionTransition) => void;
  onAdvanceStage: (leadId: number, id: number, to: LeadStageTarget) => void;
  /** STITCH-2: plain click opens the ONE LeadQuickView drawer; modifier
   *  clicks keep the canonical deep link. Absent => plain link (tests). */
  onQuickView?: (row: FollowupRow) => void;
}): JSX.Element {
  const reception = useDashboardMeContext()?.me?.role === 'receptionist';
  const open = isOpenStatus(row.status);
  const badge = statusBadge(row.status);
  // F3: every row is a door (drills to the canonical Lead 360) and explains
  // itself (a plain-language WHY + a one-line next-action, from existing fields).
  const guidance = workboardGuidance(row.reason);
  // STITCH-2 SCOPE 5: the strip derives from the lead's CURRENT stage.
  const stageActions = stageAwareActions(row.leadStage);
  return (
    <tr className={open ? undefined : 'workboard__row--done'}>
      <td>
        {reception ? <AcButton variant="ghost" className="workboard__lead-name" onClick={() => onQuickView?.(row)} disabled={!onQuickView}>{row.patientName || 'Unknown patient'}</AcButton> : <Link
          className="workboard__lead-name"
          to={workboardLeadHref(row.leadId)}
          onClick={
            onQuickView !== undefined
              ? (e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                  e.preventDefault();
                  onQuickView(row);
                }
              : undefined
          }
        >
          {row.patientName || 'Unknown patient'}
        </Link>}
        <span className="workboard__lead-id">#{row.leadId}</span>
      </td>
      <td>
        <span className="workboard__reason">{row.reason ?? 'Task'}</span>
        <span className="workboard__why">{guidance.why}</span>
        <span className="workboard__next">{reception ? 'Complete the assigned task, then mark Done.' : guidance.nextAction}</span>
      </td>
      <td data-testid="workboard-row-stage">
        {row.leadStage && ROW_STAGE_LABEL[row.leadStage] ? (
          <span className="ac-badge ac-badge--brand">
            {ROW_STAGE_LABEL[row.leadStage]}
          </span>
        ) : (
          <span className="workboard__stage-unknown">-</span>
        )}
      </td>
      <td>{row.channel}</td>
      <td className="workboard__cell--num">
        {formatRelativeAge(row.createdAt, nowMs)}
      </td>
      <td>
        <span className={badge.className}>{badge.label}</span>
      </td>
      <td>
        {open ? (
          <div className="workboard__actions">
            {reception && row.channel.toLowerCase() === 'call' && Number.isSafeInteger(row.leadId) && row.leadId > 0 ? (
              <LeadContactRevealControl key={`${row.id}:${row.leadId}`}
                availability="unknown" allowAttemptWhenUnknown polished
                initialPurpose="follow_up_call" followupTriggerLabel="Call"
                followupPatientName={row.patientName || 'Unknown patient'}
                followupContext={row.reason} followupBusy={busy} followupNotice={notice}
                followupFooter={<div className="workboard__actions">
                  <AcButton size="sm" disabled={busy} onClick={() => onTransition(row.id, 'done')}>{busy ? 'Saving…' : 'Done'}</AcButton>
                  <AcButton variant="ghost" size="sm" disabled={busy} onClick={() => onTransition(row.id, 'dismissed')}>Dismiss</AcButton>
                </div>}
                reveal={(purpose) => revealLeadContact(String(row.leadId), purpose)}
              />
            ) : null}
            <div className="workboard__action-group">
              <button
                type="button"
                className="ac-btn ac-btn--secondary ac-btn--sm"
                disabled={busy}
                onClick={() => onTransition(row.id, 'done')}
              >
                Done
              </button>
              <button
                type="button"
                className="ac-btn ac-btn--secondary ac-btn--sm"
                disabled={busy}
                onClick={() => onTransition(row.id, 'dismissed')}
              >
                Dismiss
              </button>
            </div>
            {stageActions.length > 0 ? (
              <div
                className="workboard__action-group"
                role="group"
                aria-label="Advance stage"
              >
                <span className="workboard__action-label">Advance</span>
                {stageActions.map((t) => (
                  <button
                    key={t.to}
                    type="button"
                    className={
                      t.primary
                        ? 'ac-btn ac-btn--primary ac-btn--sm'
                        : 'ac-btn ac-btn--secondary ac-btn--sm'
                    }
                    disabled={busy}
                    onClick={() => onAdvanceStage(row.leadId, row.id, t.to)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <span className="workboard__action-done">{badge.label}</span>
        )}
      </td>
    </tr>
  );
}

// --- Pure presentational board ---------------------------------------------

export function WorkboardContent({
  state,
  filters,
  busyId,
  feedback,
  nowMs,
  onFilterChange,
  onTransition,
  onAdvanceStage,
  onRetry,
  onRefresh,
  onQuickView,
}: WorkboardContentProps): JSX.Element {
  const reception = useDashboardMeContext()?.me?.role === 'receptionist';
  const counts =
    state.kind === 'ready' ? summarizeWorkActions(state.rows) : null;

  return (
    <div className="dashboard workboard">
      <div className="page-header">
        <h1>{reception ? 'Assigned tasks' : 'My Workboard'}</h1>
        {!reception ? <p className="page-header__sub">Assigned follow-ups and lead actions</p> : null}
        {counts && !reception ? (
          <p className="page-header__sub">
            {counts.open} open &middot; {counts.completed} completed &middot;{' '}
            {counts.total} total
          </p>
        ) : null}
      </div>

      {state.kind === 'loading' ? (
        <div className="loading" role="status" data-testid="workboard-loading">
          Loading your workboard&hellip;
        </div>
      ) : state.kind === 'error' ? (
        <div data-testid="workboard-error">
          <div className="mb-4">
            <DashboardLoadError message={state.message} />
          </div>
          <button
            type="button"
            className="ac-btn ac-btn--secondary ac-btn--sm"
            onClick={onRetry}
          >
            Retry
          </button>
        </div>
      ) : (
        <ReadyBoard
          rows={state.rows}
          counts={counts!}
          filters={filters}
          busyId={busyId}
          feedback={feedback}
          nowMs={nowMs}
          onFilterChange={onFilterChange}
          onTransition={onTransition}
          onAdvanceStage={onAdvanceStage}
          onRefresh={onRefresh}
          onQuickView={onQuickView}
        />
      )}
    </div>
  );
}

function ReadyBoard({
  rows,
  counts,
  filters,
  busyId,
  feedback,
  nowMs,
  onFilterChange,
  onTransition,
  onAdvanceStage,
  onRefresh,
  onQuickView,
}: {
  rows: FollowupRow[];
  counts: ReturnType<typeof summarizeWorkActions>;
  filters: WorkboardFilters;
  busyId: number | null;
  feedback: WorkboardFeedback | null;
  nowMs: number;
  onFilterChange: (filters: WorkboardFilters) => void;
  onTransition: (id: number, status: WorkActionTransition) => void;
  onAdvanceStage: (leadId: number, id: number, to: LeadStageTarget) => void;
  onRefresh: () => void;
  onQuickView?: (row: FollowupRow) => void;
}): JSX.Element {
  const feedbackRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (feedback?.kind === 'success') feedbackRef.current?.focus();
  }, [feedback]);
  const reasons = distinctReasons(rows);
  const visible = filterWorkActions(rows, filters);

  return (
    <>
      <div className="quick-stats" data-testid="workboard-counts">
        <div className="quick-stat">
          <span className="quick-stat__value">{counts.open}</span>
          <span className="quick-stat__label">Open</span>
        </div>
        <div className="quick-stat">
          <span className="quick-stat__value">{counts.completed}</span>
          <span className="quick-stat__label">Completed</span>
        </div>
        <div className="quick-stat">
          <span className="quick-stat__value">{counts.total}</span>
          <span className="quick-stat__label">Total assigned</span>
        </div>
      </div>

      {feedback ? (
        <div
          className={
            feedback.kind === 'success'
              ? 'workboard__feedback workboard__feedback--success'
              : 'workboard__feedback workboard__feedback--error'
          }
          role="status"
          aria-live="polite"
          data-testid="workboard-feedback"
          ref={feedbackRef}
          tabIndex={-1}
        >
          <span>{feedback.text}</span>
          {feedback.showRefresh ? (
            <button
              type="button"
              className="ac-btn ac-btn--secondary ac-btn--sm"
              onClick={onRefresh}
            >
              Refresh
            </button>
          ) : null}
        </div>
      ) : null}

      <section className="clinic-table">
        <h2 className="clinic-table__title">Work queue</h2>

        <div className="workboard__toolbar">
          <div className="workboard__filter-group" role="group" aria-label="Filter by status">
            <span className="workboard__filter-label">Status</span>
            <div className="workboard__pills">
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={pillClass(filters.status === opt.value)}
                  aria-pressed={filters.status === opt.value}
                  data-testid={`workboard-status-${opt.value}`}
                  onClick={() =>
                    onFilterChange({ ...filters, status: opt.value })
                  }
                >
                  {opt.label}
                  <span className="workboard__pill-count">
                    {countByStatusFilter(rows, opt.value)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="workboard__filter-group" role="group" aria-label="Filter by action type">
            <span className="workboard__filter-label">Action type</span>
            <div className="workboard__pills">
              <button
                type="button"
                className={pillClass(filters.reason === null)}
                aria-pressed={filters.reason === null}
                onClick={() => onFilterChange({ ...filters, reason: null })}
              >
                All
                <span className="workboard__pill-count">
                  {countByReason(rows, filters.status, null)}
                </span>
              </button>
              {reasons.map((reason) => (
                <button
                  key={reason}
                  type="button"
                  className={pillClass(filters.reason === reason)}
                  aria-pressed={filters.reason === reason}
                  onClick={() => onFilterChange({ ...filters, reason })}
                >
                  {reason}
                  <span className="workboard__pill-count">
                    {countByReason(rows, filters.status, reason)}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="workboard__filter-group workboard__filter-group--search">
            <label className="workboard__filter-label" htmlFor="workboard-search">
              Search
            </label>
            <input
              id="workboard-search"
              type="search"
              className="workboard__search"
              placeholder="Search by name or lead #"
              value={filters.search}
              onChange={(e) =>
                onFilterChange({ ...filters, search: e.target.value })
              }
            />
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="empty-state" data-testid="workboard-empty">
            No assigned work right now.
          </div>
        ) : visible.length === 0 ? (
          <div className="empty-state" data-testid="workboard-empty-filtered">
            No work matches these filters.
          </div>
        ) : (
          <div className="workboard__table-wrap">
            <table className="table" data-testid="workboard-table">
              <thead>
                <tr>
                  <th>Lead</th>
                  <th>Action</th>
                  <th>Stage</th>
                  <th>Channel</th>
                  <th>Age</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <WorkboardRow
                    key={String(row.id)}
                    row={row}
                    busy={busyId === row.id}
                    nowMs={nowMs}
                    onTransition={onTransition}
                    onAdvanceStage={onAdvanceStage}
                    onQuickView={onQuickView}
                    notice={feedback?.kind === 'error' ? feedback.text : null}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

/** Reuse the same authorized patient search; task data and actions stay in their owner. */
function ReceptionTaskSearch({panelTarget,onPanelChange,onChanged,disabled}: {
  panelTarget:HTMLDivElement|null;onPanelChange:(open:boolean)=>void;onChanged:()=>void;disabled:boolean;
}) {
  const me=useDashboardMeContext()?.me;
  const {user,loading}=useAuth();
  const clinic=useClinicContext();
  const navigate=useNavigate();
  const clinicId=clinic?.selectedClinicId??null;
  const option=clinic?.options.find(row=>row.clinicId===clinicId);
  const scope=`${user?.id}:${me?.organizationId}:${me?.role}:${clinicId}:${me?.patientMasterEnabled}:${me?.clinicIds.join(',')}`;
  return <RoleHomeSearchPortal><ReceptionSearch key={scope} scopeKey={scope} recordOrigin="workboard"
    clinicId={clinicId} clinicLabel={option?clinicOptionLabel(option):'Clinic'}
    disabled={disabled||loading||!user||me?.role!=='receptionist'||me.patientMasterEnabled!==true||clinicId===null}
    panelTarget={panelTarget} onPanelChange={onPanelChange} onChanged={onChanged}
    onAppointment={(id,iso)=>navigate(`${walkinBoardHref(iso,id)}&quickAppointment=${id}`)} /></RoleHomeSearchPortal>;
}

// --- Stateful wirer (default export) ---------------------------------------

export default function WorkboardPage(): JSX.Element {
  const reception = useDashboardMeContext()?.me?.role === 'receptionist';
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);
  const [searchPanelTarget, setSearchPanelTarget] = useState<HTMLDivElement | null>(null);
  const [state, setState] = useState<WorkboardPanelState>({ kind: 'loading' });
  const [filters, setFilters] = useState<WorkboardFilters>(
    DEFAULT_WORKBOARD_FILTERS,
  );
  const [busyId, setBusyId] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<WorkboardFeedback | null>(null);
  // STITCH-2: the row-click quick view (the ONE drawer).
  const [quickViewRow, setQuickViewRow] = useState<FollowupRow | null>(null);
  const quickViewOpener = useRef<HTMLElement | null>(null);
  const openQuickView = (row: FollowupRow) => {
    quickViewOpener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setQuickViewRow(row);
  };
  const closeQuickView = () => {
    setQuickViewRow(null);
    if (quickViewOpener.current?.isConnected) quickViewOpener.current.focus();
  };

  const load = useCallback(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    setFeedback(null);
    fetchMyWorkActions()
      .then((rows) => {
        if (!cancelled) setState({ kind: 'ready', rows });
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setState({
            kind: 'error',
            message:
              e instanceof Error ? e.message : 'Failed to load your workboard',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  const onTransition = useCallback(
    (id: number, status: WorkActionTransition) => {
      setBusyId(id);
      setFeedback(null);
      transitionWorkAction(id, status)
        .then((res) => {
          setState((prev) =>
            prev.kind === 'ready'
              ? { kind: 'ready', rows: applyStatusToRow(prev.rows, id, res.status) }
              : prev,
          );
          setFeedback({
            kind: 'success',
            text:
              status === 'done'
                ? 'Follow-up marked done.'
                : 'Follow-up dismissed.',
          });
        })
        .catch((err: unknown) => {
          setFeedback({
            kind: 'error',
            text: workboardActionErrorMessage(err),
            showRefresh: isConflictError(err),
          });
        })
        .finally(() => {
          setBusyId((b) => (b === id ? null : b));
        });
    },
    [],
  );

  const onAdvanceStage = useCallback(
    (leadId: number, id: number, to: LeadStageTarget) => {
      setBusyId(id);
      setFeedback(null);
      advanceLeadStage(leadId, to)
        .then((res) => {
          // A successful front-half advance closes the lead's open work item
          // server-side, so EVERY open row of this lead leaves the open queue
          // (closeOpenWorkItemsForLead closes them all) - reflect them as done
          // and carry the new stage so the strip re-derives (STITCH-2 SCOPE 5).
          setState((prev) =>
            prev.kind === 'ready'
              ? {
                  kind: 'ready',
                  rows: applyStageAdvanceToRows(prev.rows, leadId, res.toStage),
                }
              : prev,
          );
          setFeedback({
            kind: 'success',
            text: `Lead #${leadId} moved to ${stageTargetLabel(res.toStage)}.`,
          });
        })
        .catch((err: unknown) => {
          setFeedback({
            kind: 'error',
            text: workboardActionErrorMessage(err),
            showRefresh: isConflictError(err),
          });
        })
        .finally(() => {
          setBusyId((b) => (b === id ? null : b));
        });
    },
    [],
  );

  const board = (
    <WorkboardContent
        state={state}
        filters={filters}
        busyId={busyId}
        feedback={feedback}
        nowMs={Date.now()}
        onFilterChange={setFilters}
        onTransition={onTransition}
        onAdvanceStage={onAdvanceStage}
        onRetry={load}
        onRefresh={load}
        onQuickView={openQuickView}
      />
  );
  return (
    <div className={reception ? 'ac-page today-home reception-assigned-workspace' : undefined}>
      {reception ? <ReceptionTaskSearch panelTarget={searchPanelTarget} onPanelChange={setSearchPanelOpen}
        onChanged={load} disabled={quickViewRow !== null || busyId !== null} /> : null}
      {reception ? <div className="ac-workspace-panels">
      <MasterDetail single={!searchPanelOpen}>
      {board}
      <DetailAside label="Selected patient"><div ref={setSearchPanelTarget} /></DetailAside>
      </MasterDetail>
      </div> : board}
      {quickViewRow !== null ? (
        <LeadQuickViewHost
          leadId={quickViewRow.leadId}
          assignedTaskId={quickViewRow.id}
          seedTitle={quickViewRow.patientName || 'Unknown patient'}
          onClose={closeQuickView}
          onChanged={() => load()}
          stageStrip={(model, reload) =>
            model.leadId !== null ? (
              <QuickViewStageStrip
                stage={model.stage}
                onAdvance={async (to) => {
                  const res = await advanceLeadStage(model.leadId as number, to);
                  // Mirror the server: every open task of this lead is done.
                  setState((prev) =>
                    prev.kind === 'ready'
                      ? {
                          kind: 'ready',
                          rows: applyStageAdvanceToRows(
                            prev.rows,
                            model.leadId as number,
                            res.toStage,
                          ),
                        }
                      : prev,
                  );
                  setFeedback({
                    kind: 'success',
                    text: `Lead #${model.leadId} moved to ${stageTargetLabel(res.toStage)}.`,
                  });
                  reload();
                }}
              />
            ) : null
          }
        />
      ) : null}
    </div>
  );
}
